import React from 'react';
import styled from 'styled-components';

import SuccessAnimation from '#/components/basic/animations/SuccessAnimation';
import Button from '#/components/basic/Button/Button';
import ButtonRouterLink from '#/components/basic/Button/ButtonRouterLink';
import ModalContent from '#/components/basic/modal/components/ModalContent';
import ModalFooter from '#/components/basic/modal/components/ModalFooter';
import ModalFooterButtons from '#/components/basic/modal/components/ModalFooterButtons';
import config from '#/config';
import { ChainsFromConfig } from '#/config/configType';
import { getBlockExplorerTransactionUrl } from '#/config/configUtils';
import { chainsDepositTimeMinMap } from '#/config/constants';
import { openExternalUrl, redirectTo } from '#/lib/redirect';
import { useSelector } from '#/reduxState/storeCore';

const Wrap = styled.div`
  text-align: center;
  padding-top: 16px;
`;

const Divider = styled.div`
  height: 20px;
`;

const Heading = styled.h2`
  padding: 0;
  margin: 8px;
  font-size: var(--medium2);
`;

const MoreInfoText = styled.p`
  font-size: var(--small);
  color: var(--gray4c);
  text-align: center;
  line-height: 1.5;
  padding: 0;
  margin: 8px 0 4px;
`;

export function ActionVaultConfigUpdateSuccessModal({
  onClose,
}: {
  onClose: () => void;
}) {
  return (
    <>
      <ModalContent>
        <Wrap>
          <SuccessAnimation size={140} />
          <Heading>Configuration Update Success</Heading>
        </Wrap>
      </ModalContent>
      <ModalFooterButtons
        confirmButtonProps={{
          children: 'Back to Configuration',
          bordered: true,
          onClick: onClose,
        }}
      />
    </>
  );
}

function ActionSuccessModalContent({
  type,
  onClose,
  balanceHistoryPath,
  chain,
  transactionHash,
}: {
  type:
    | 'Deposit'
    | 'Withdrawal'
    | 'VaultDeposit'
    | 'VaultLoopBackDeposit'
    | 'VaultWithdrawal'
    | 'VaultWithdrawalQueued'
    | 'Transfer'
    | 'RewardClaim'
    | 'RewardClaimOnExchange'
    | 'Unlock';
  balanceHistoryPath?: string;
  chain: ChainsFromConfig;
  onClose?: () => void;
  transactionHash?: string | true;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const isBridged = chain !== 'katanaChain';

  if (type === 'Unlock') {
    return (
      <ModalContent>
        <Wrap>
          <SuccessAnimation size={90} />
          <Heading>Success, your wallet was unlocked!</Heading>
          <Divider />
          <Divider />
        </Wrap>
      </ModalContent>
    );
  }

  if (type === 'RewardClaimOnExchange') {
    return (
      <>
        <ModalContent>
          <Wrap>
            <SuccessAnimation size={90} />
            <Heading>Claim initiated</Heading>
          </Wrap>
        </ModalContent>
        <ModalFooter alignRightOnDesktop>
          <ButtonRouterLink
            inline={!isMobile}
            widePadding
            bordered
            to={balanceHistoryPath ?? '/wallet/history/deposits-withdrawals'}
          >
            View Transactions
          </ButtonRouterLink>
        </ModalFooter>
      </>
    );
  }

  if (type === 'RewardClaim') {
    return (
      <>
        <ModalContent>
          <Wrap>
            <SuccessAnimation size={90} />
            <Heading>Claim initiated</Heading>
          </Wrap>
        </ModalContent>
        <ModalFooterButtons
          confirmButtonProps={{
            bordered: true,
            onClick: () => {
              if (!transactionHash || typeof transactionHash !== 'string') {
                alert('Missing transaction hash');
                return;
              }
              openExternalUrl(
                getBlockExplorerTransactionUrl(transactionHash, chain)
              );
            },
            children: 'View Transaction',
          }}
        />
      </>
    );
  }

  const balanceHistoryModalFooter = (
    <ModalFooterButtons
      confirmButtonProps={{
        children: 'Check Status',
        bordered: true,
        onClick: () => {
          if (!isMobile) {
            // Closing on mobile would call redirection
            onClose?.();
          }
          redirectTo(
            balanceHistoryPath ?? '/wallet/history/deposits-withdrawals'
          );
        },
      }}
    />
  );

  if (
    type === 'Deposit' ||
    type === 'VaultDeposit' ||
    type === 'VaultLoopBackDeposit'
  ) {
    return (
      <>
        <ModalContent>
          <Wrap>
            <SuccessAnimation size={90} />
            <Heading>Your deposit has been initiated.</Heading>
            {type === 'VaultLoopBackDeposit' ? (
              <MoreInfoText>
                Deposits typically take 30s to complete.
              </MoreInfoText>
            ) : (
              isBridged && (
                <MoreInfoText>
                  {config.chains[chain].chainNetwork} deposits typically take ~
                  {chainsDepositTimeMinMap[chain]} min.
                </MoreInfoText>
              )
            )}
          </Wrap>
        </ModalContent>
        {balanceHistoryModalFooter}
      </>
    );
  }

  if (
    type === 'Withdrawal' ||
    type === 'VaultWithdrawal' ||
    type === 'VaultWithdrawalQueued'
  ) {
    return (
      <>
        <ModalContent>
          <Wrap>
            <SuccessAnimation size={140} />
            <Heading>Your withdrawal has been initiated.</Heading>
            {type === 'VaultWithdrawalQueued' ? (
              <MoreInfoText>
                Your withdrawal has been queued and Manager has been notified
              </MoreInfoText>
            ) : (
              isBridged && (
                <MoreInfoText>
                  Bridge transaction may still be in flight.
                </MoreInfoText>
              )
            )}
          </Wrap>
        </ModalContent>
        {balanceHistoryModalFooter}
      </>
    );
  }

  if (type === 'Transfer') {
    // Transfer unused - unmaintaned
    return (
      <Wrap>
        <SuccessAnimation size={140} />
        <Heading>Your transfer has been initialized.</Heading>
        <Divider />
        <Divider />
        {transactionHash && typeof transactionHash === 'string' ? (
          <Button
            onClick={() => {
              console.log('Opening TX link for hash', transactionHash);
              openExternalUrl(
                getBlockExplorerTransactionUrl(transactionHash, chain)
              );
            }}
          >
            View Transaction
          </Button>
        ) : (
          balanceHistoryModalFooter
        )}
      </Wrap>
    );
  }

  return null;
}

export default ActionSuccessModalContent;
